import { z } from "zod";

import { OpaqueIdSchema } from "./identity-schemas";

const EmailAddressSchema = z.email().trim().toLowerCase().max(320);
const TimestampSchema = z.number().int().positive();

const BaseArtifactMetadataSchema = z.strictObject({
  title: z.string().trim().min(1).max(240),
  filename: z.string().trim().min(1).max(255),
  mediaType: z.string().trim().toLowerCase().min(3).max(120),
  byteCount: z
    .number()
    .int()
    .min(1)
    .max(100 * 1024 * 1024),
  sourceCreatedAt: TimestampSchema.nullable().default(null),
  sourceModifiedAt: TimestampSchema.nullable().default(null),
  sourceSystem: z.string().trim().min(1).max(80).nullable().default(null),
  sourceRecordId: z.string().trim().min(1).max(255).nullable().default(null),
  toolId: OpaqueIdSchema.nullable().optional(),
});

export const ArtifactMetadataSchema = z.discriminatedUnion("sourceType", [
  BaseArtifactMetadataSchema.extend({
    sourceType: z.literal("email"),
    email: z.strictObject({
      from: EmailAddressSchema,
      to: z.array(EmailAddressSchema).min(1).max(100),
      cc: z.array(EmailAddressSchema).max(100).default([]),
      bcc: z.array(EmailAddressSchema).max(100).default([]),
      subject: z.string().trim().min(1).max(998),
      messageId: z.string().trim().min(1).max(998).nullable().default(null),
      inReplyTo: z.string().trim().min(1).max(998).nullable().default(null),
      references: z.array(z.string().trim().min(1).max(998)).max(100).default([]),
      sentAt: TimestampSchema,
    }),
  }),
  BaseArtifactMetadataSchema.extend({
    sourceType: z.literal("transcript"),
    transcript: z.strictObject({
      callStartedAt: TimestampSchema,
      durationSeconds: z.number().int().positive().max(86_400).nullable().default(null),
      participants: z
        .array(
          z.strictObject({
            displayName: z.string().trim().min(1).max(120),
            email: EmailAddressSchema.nullable().default(null),
          }),
        )
        .min(1)
        .max(100),
    }),
  }),
  ...(["file", "image", "video", "other"] as const).map((sourceType) =>
    BaseArtifactMetadataSchema.extend({ sourceType: z.literal(sourceType) }),
  ),
]);
export type ArtifactMetadata = z.infer<typeof ArtifactMetadataSchema>;

export const ArtifactScopeSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("customer") }),
  z.strictObject({ kind: z.literal("tool"), toolId: OpaqueIdSchema }),
]);
export type ArtifactScope = z.infer<typeof ArtifactScopeSchema>;

export const ArtifactUploadPreparedSchema = z.strictObject({
  artifactId: OpaqueIdSchema,
  uploadToken: z.string().min(32).max(256),
  expiresAt: TimestampSchema,
});

export const CustomerContextSearchRequestSchema = z.strictObject({
  includeCoverage: z.boolean().optional(),
  query: z.string().trim().min(2).max(2_000),
  limit: z.number().int().min(1).max(20).default(10),
  scope: ArtifactScopeSchema.default({ kind: "customer" }),
});

export const CustomerContextSearchResultSchema = z.strictObject({
  artifactId: OpaqueIdSchema,
  title: z.string().min(1).max(240),
  filename: z.string().min(1).max(255),
  sourceType: z.enum(["email", "transcript", "file", "image", "video", "other"]),
  toolId: OpaqueIdSchema.nullable(),
  score: z.number().min(0).max(1),
  snippet: z.string().max(4_000),
  previewUrl: z.url(),
  downloadUrl: z.url(),
});

export const CustomerContextSearchResponseSchema = z.strictObject({
  query: z.string().min(1).max(2_000),
  results: z.array(CustomerContextSearchResultSchema).max(20),
  coverage: z.strictObject({
    state: z.enum(["ready", "indexing", "partial", "unavailable", "unsupported"]),
    mode: z.enum(["hybrid", "metadata"]),
    reasons: z
      .array(
        z.enum([
          "indexing_pending",
          "index_failed",
          "unsupported_sources",
          "provider_unavailable",
          "scope_filter_unsupported",
          "source_mismatch",
          "index_status_unverified",
          "metadata_only",
        ]),
      )
      .max(8),
    totalSources: z.number().int().nonnegative(),
    submittedSources: z.number().int().nonnegative(),
    completedSources: z.number().int().nonnegative().nullable(),
    pendingSources: z.number().int().nonnegative(),
    failedSources: z.number().int().nonnegative(),
    unsupportedSources: z.number().int().nonnegative(),
  }),
});

export const ArtifactSchema = z.strictObject({
  id: OpaqueIdSchema,
  sourceType: z.enum(["email", "transcript", "file", "image", "video", "other"]),
  title: z.string().min(1).max(240),
  filename: z.string().min(1).max(255),
  mediaType: z.string().min(3).max(120),
  byteCount: z.number().int().positive(),
  sha256: z
    .string()
    .length(64)
    .regex(/^[0-9a-f]+$/),
  sourceCreatedAt: TimestampSchema.nullable(),
  sourceModifiedAt: TimestampSchema.nullable(),
  ingestedAt: TimestampSchema,
  processingStatus: z.enum(["pending", "ready", "failed"]),
  searchStatus: z.enum(["pending", "indexed", "unsupported", "failed"]),
  metadata: ArtifactMetadataSchema,
});
export type Artifact = z.infer<typeof ArtifactSchema>;

export const ArtifactResponseSchema = z.strictObject({ artifact: ArtifactSchema });
export const ArtifactsResponseSchema = z.strictObject({
  artifacts: z.array(ArtifactSchema).max(200),
});
